import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/App';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { CONSULTANT_PROFILE, BOOKING } from '@/constants/testIds';
import Navigation from '@/components/Navigation';
import StarRating from '@/components/StarRating';
import { ArrowLeft, Mail, Phone, Building2, Briefcase, DollarSign, Calendar, MessageSquare } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

export default function ConsultantProfile() {
  const { id } = useParams();
  const { token } = useAuth();
  const navigate = useNavigate();
  const [consultant, setConsultant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [bookingForm, setBookingForm] = useState({
    service_type: 'Building Defects',
    preferred_date: '',
    description: '',
    budget: 150.0
  });

  const fetchConsultant = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/consultants/${id}`);
      setConsultant(response.data);
    } catch (error) {
      console.error('Failed to fetch consultant:', error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchConsultant();
  }, [fetchConsultant]);

  const handleSubmitBooking = async (e) => {
    e.preventDefault();
    try {
      await axios.post(
        `${API}/bookings`,
        { ...bookingForm, consultant_email: consultant.email },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setShowBookingModal(false);
      navigate('/bookings');
    } catch (error) {
      console.error('Failed to create booking:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navigation />
        <div className="flex items-center justify-center h-96">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-black"></div>
        </div>
      </div>
    );
  }

  if (!consultant) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navigation />
        <div className="max-w-7xl mx-auto px-6 py-8 mt-16 text-center">
          <p className="text-slate-600">Consultant not found</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Navigation />

      <div className="max-w-7xl mx-auto px-6 py-8 mt-16">
        <button
          onClick={() => navigate('/consultants')}
          className="flex items-center gap-2 text-slate-600 hover:text-black mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Consultants
        </button>

        {/* Profile Header */}
        <div className="grid-border bg-white p-8 mb-6">
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6">
            <div className="flex gap-6">
              <div className="w-24 h-24 bg-slate-200 rounded-sm flex items-center justify-center flex-shrink-0">
                <span className="text-3xl font-bold text-slate-600" style={{ fontFamily: 'Outfit' }}>
                  {consultant.name.charAt(0).toUpperCase()}
                </span>
              </div>
              <div className="flex-1">
                <h1 className="text-3xl font-bold tracking-tight text-black mb-2" style={{ fontFamily: 'Outfit' }}>
                  {consultant.name}
                </h1>
                <div className="flex items-center gap-4 mb-3">
                  <StarRating value={consultant.ratings_avg || 0} size="md" />
                  <span className="text-sm text-slate-600">
                    {consultant.ratings_avg > 0
                      ? `${consultant.ratings_avg} (${consultant.ratings_count} ${consultant.ratings_count === 1 ? 'review' : 'reviews'})`
                      : 'No reviews yet'}
                  </span>
                </div>
                {consultant.organization && (
                  <p className="text-sm text-slate-600 flex items-center gap-2 mb-1">
                    <Building2 className="w-4 h-4" />
                    {consultant.organization}
                  </p>
                )}
                <p className="text-sm text-slate-600 flex items-center gap-2 mb-1">
                  <Mail className="w-4 h-4" />
                  {consultant.email}
                </p>
                {consultant.phone && (
                  <p className="text-sm text-slate-600 flex items-center gap-2">
                    <Phone className="w-4 h-4" />
                    {consultant.phone}
                  </p>
                )}
              </div>
            </div>

            <div className="flex flex-col items-end gap-3">
              {consultant.hourly_rate && (
                <div className="text-right">
                  <p className="text-2xl font-bold" style={{ fontFamily: 'Outfit' }}>
                    A${consultant.hourly_rate.toFixed(0)}<span className="text-base text-slate-500">/hr</span>
                  </p>
                </div>
              )}
              <button
                data-testid={CONSULTANT_PROFILE.bookButton}
                onClick={() => setShowBookingModal(true)}
                className="px-8 py-3 bg-black text-white rounded-sm hover:bg-slate-800 transition-all font-medium"
              >
                Book Consultation
              </button>
            </div>
          </div>

          {consultant.bio && (
            <div className="mt-6 pt-6 border-t border-slate-200">
              <h3 className="text-sm font-semibold text-black mb-2" style={{ fontFamily: 'Outfit' }}>About</h3>
              <p className="text-sm text-slate-700 leading-relaxed">{consultant.bio}</p>
            </div>
          )}

          {consultant.expertise && consultant.expertise.length > 0 && (
            <div className="mt-4 pt-4 border-t border-slate-200">
              <h3 className="text-sm font-semibold text-black mb-2 flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
                <Briefcase className="w-4 h-4" />
                Expertise
              </h3>
              <div className="flex flex-wrap gap-2">
                {consultant.expertise.map((skill) => (
                  <span key={skill} className="px-3 py-1 bg-blue-50 text-blue-800 text-xs rounded-sm border border-blue-200">
                    {skill}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Reviews */}
          <div data-testid={CONSULTANT_PROFILE.ratingsSection} className="lg:col-span-2 grid-border bg-white p-6">
            <h2 className="text-xl font-semibold mb-4 flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
              <MessageSquare className="w-5 h-5" />
              Reviews ({consultant.ratings_count})
            </h2>
            {!consultant.reviews || consultant.reviews.length === 0 ? (
              <p className="text-sm text-slate-500 py-8 text-center">No reviews yet. Be the first to book and review!</p>
            ) : (
              <div className="space-y-4">
                {consultant.reviews.map((review) => (
                  <div key={review.id} className="border-b border-slate-200 pb-4 last:border-0 last:pb-0">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <p className="font-medium text-black">{review.client_name}</p>
                        <p className="text-xs text-slate-500">{new Date(review.created_at).toLocaleDateString()}</p>
                      </div>
                      <StarRating value={review.stars} size="sm" />
                    </div>
                    <p className="text-sm text-slate-700">{review.comment}</p>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Sidebar: Availability + Portfolio */}
          <div className="space-y-6">
            {/* Availability */}
            <div data-testid={CONSULTANT_PROFILE.availabilitySection} className="grid-border bg-white p-6">
              <h2 className="text-lg font-semibold mb-4 flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
                <Calendar className="w-5 h-5" />
                Availability
              </h2>
              {consultant.availability && Object.keys(consultant.availability).length > 0 ? (
                <div className="space-y-2">
                  {DAYS.map((day) => {
                    const slots = consultant.availability[day.toLowerCase()];
                    return (
                      <div key={day} className="flex justify-between items-center text-sm py-1">
                        <span className="font-medium text-black">{day}</span>
                        <span className="text-slate-600">
                          {slots && slots.length > 0 ? slots.join(', ') : 'Closed'}
                        </span>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-sm text-slate-500">Availability on request</p>
              )}
            </div>

            {/* Portfolio */}
            <div data-testid={CONSULTANT_PROFILE.portfolioSection} className="grid-border bg-white p-6">
              <h2 className="text-lg font-semibold mb-4 flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
                <Briefcase className="w-5 h-5" />
                Recent Work
              </h2>
              {!consultant.portfolio || consultant.portfolio.length === 0 ? (
                <p className="text-sm text-slate-500">No completed projects yet</p>
              ) : (
                <div className="space-y-3">
                  {consultant.portfolio.map((item, idx) => (
                    <div key={`${item.created_at}-${idx}`} className="text-sm">
                      <p className="font-medium text-black">{item.service_type.replace(/_/g, ' ')}</p>
                      <p className="text-xs text-slate-600 line-clamp-2 mt-1">{item.description}</p>
                      <p className="text-xs text-slate-400 mt-1">{item.preferred_date}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Booking Modal */}
      {showBookingModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-6">
          <div className="bg-white rounded-sm max-w-2xl w-full max-h-[90vh] overflow-y-auto p-8">
            <h2 className="text-2xl font-bold mb-6" style={{ fontFamily: 'Outfit' }}>
              Book with {consultant.name}
            </h2>
            <form onSubmit={handleSubmitBooking} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-black mb-2">Service Type</label>
                <select
                  data-testid={BOOKING.serviceTypeSelect}
                  value={bookingForm.service_type}
                  onChange={(e) => setBookingForm({ ...bookingForm, service_type: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:ring-2 focus:ring-blue-600"
                >
                  <option value="Building Defects">Building Defects</option>
                  <option value="Specialist Services">Specialist Services</option>
                  <option value="Construction Sequencing">Construction Sequencing</option>
                  <option value="Cost Management">Cost Management</option>
                  <option value="DIY Projects">DIY Projects</option>
                  <option value="Trade Works & Administration" disabled>Trade Works & Administration — Coming Soon</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-black mb-2">Preferred Date</label>
                <input
                  data-testid={BOOKING.dateInput}
                  type="date"
                  value={bookingForm.preferred_date}
                  onChange={(e) => setBookingForm({ ...bookingForm, preferred_date: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:ring-2 focus:ring-blue-600"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-black mb-2">Description</label>
                <textarea
                  data-testid={BOOKING.descriptionInput}
                  value={bookingForm.description}
                  onChange={(e) => setBookingForm({ ...bookingForm, description: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:ring-2 focus:ring-blue-600"
                  rows="4"
                  placeholder="Describe your needs..."
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-black mb-2">Budget (USD)</label>
                <input
                  data-testid={BOOKING.budgetInput}
                  type="number"
                  step="0.01"
                  value={bookingForm.budget}
                  onChange={(e) => setBookingForm({ ...bookingForm, budget: parseFloat(e.target.value) })}
                  className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:ring-2 focus:ring-blue-600"
                  required
                />
              </div>

              <div className="flex gap-4 pt-4">
                <button
                  type="button"
                  onClick={() => setShowBookingModal(false)}
                  className="flex-1 px-6 py-3 border border-slate-300 rounded-sm hover:bg-slate-50 transition-all"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-6 py-3 bg-black text-white rounded-sm hover:bg-slate-800 transition-all"
                >
                  Book Now
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
